import { z } from 'zod';
import { HerkunftFelder } from './auftrag-zusammenfuehren.js';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { TimeOfDaySchema, WeekdaySchema } from './sync-schedule.js';

// ============================================================================
// Projekt-Befunde (PV-052) — docs/features/projekt-befunde/
// ============================================================================

/** Regel-Schlüssel des Katalogs (A6). Jede Lieferung ergänzt ihre Regeln hier. */
export const BEFUND_REGEL_KEYS = [
  'mtb_briefing_fehlt',
  'schliess_check',
  'folgeprojekt_fehlt',
  // L3 (#106): Stammdaten-Regeln
  'besetzung_ohne_satz',
  'budget_fehlt',
  'abrechnungsart_gemischt',
  // L3 (#106, Nutzerentscheid 4): „final" wird dauerhaft verfehlt — zuständig KFML
  'final_blockiert',
  // L4 (#107): Float
  'float_keine_planung',
  'float_nur_reservierung',
  'float_zuordnung_unklar',
  'float_person_ohne_besetzung',
  // L4 (#107): Budget-Hochrechnung
  'budget_rechnung',
  'budget_festpreis',
  // L4 (#107, Gesamt-Review): Datenfehler, der die Budget-Hochrechnung still verfälscht
  'besetzung_intern_und_extern',
  // #131: Auftrag in Salesforce gelöscht — von der PV automatisch abgeschlossen
  'auftrag_sf_geloescht',
  // #130: Service-Auftrag hängt an einem Projekt, dessen Bereich nicht Service ist
  'service_auftrag_verdeckt',
] as const;
export const BefundRegelKeySchema = z.enum(BEFUND_REGEL_KEYS);
export type BefundRegelKey = z.infer<typeof BefundRegelKeySchema>;

export const BefundStufeSchema = z.enum(['info', 'handeln', 'kritisch']);
export type BefundStufe = z.infer<typeof BefundStufeSchema>;

/** Rangfolge für Sortierung und „höchste Stufe“ (größer = dringender). */
export const BEFUND_STUFE_RANG: Record<BefundStufe, number> = { info: 1, handeln: 2, kritisch: 3 };

export const BefundZustandSchema = z.enum(['offen', 'angefragt', 'zurueckgestellt', 'erledigt']);
export type BefundZustand = z.infer<typeof BefundZustandSchema>;

export const BefundErledigtWegSchema = z.enum(['automatisch', 'aktion']);
export type BefundErledigtWeg = z.infer<typeof BefundErledigtWegSchema>;

export const BefundVerlaufEreignisSchema = z.enum([
  'angelegt',
  'stufe_geaendert',
  'angefragt',
  'zurueckgestellt',
  'wieder_offen',
  'erledigt',
  'nachgehakt',
]);
export type BefundVerlaufEreignis = z.infer<typeof BefundVerlaufEreignisSchema>;

export const BefundZustaendigRolleSchema = z.enum(['pl', 'mtb', 'kfml']);
export type BefundZustaendigRolle = z.infer<typeof BefundZustaendigRolleSchema>;

/** Aktionen, die eine Regel anbietet (A11). Die UI blendet sie zusätzlich nach `darf` aus. */
export const BefundAktionSchema = z.enum([
  'briefing_erzeugen',
  'neu_pruefen',
  'zurueckstellen',
  'nachhaken',
  'antworten', // L2: PL-Antwort beim Schließ-Check
]);
export type BefundAktion = z.infer<typeof BefundAktionSchema>;

export const BefundLocaleSchema = z.enum(['de', 'en']);
export type BefundLocale = z.infer<typeof BefundLocaleSchema>;

// --- Daten je Regel (Spalte `befunde.daten`) --------------------------------

export const MtbBriefingFehltDatenSchema = z.object({
  phase_name: z.string().min(1),
  tage_in_phase: z.number().int().nonnegative(),
});
export type MtbBriefingFehltDaten = z.infer<typeof MtbBriefingFehltDatenSchema>;

/** `schliess_check` (A6): je Auftrag; `pv_abgeschlossen` fließt in den Satz ein. */
export const SchliessCheckDatenSchema = z.object({
  project_key: z.string().min(1),
  projektende: z.string().date(),
  tage_seit_ende: z.number().int().positive(),
  pv_abgeschlossen: z.boolean(),
});
export type SchliessCheckDaten = z.infer<typeof SchliessCheckDatenSchema>;

/** `folgeprojekt_fehlt` (A6): alle geprüften Aufträge des Projekts, alle in SF abgeschlossen. */
export const FolgeprojektFehltDatenSchema = z.object({
  auftraege: z
    .array(z.object({ project_key: z.string().min(1), projektende: z.string().date().nullable() }))
    .min(1),
  letztes_ende: z.string().date().nullable(),
});
export type FolgeprojektFehltDaten = z.infer<typeof FolgeprojektFehltDatenSchema>;

/**
 * `auftrag_sf_geloescht` (#131): je Auftrag, dessen SF-Gegenstück im Papierkorb lag und den die PV
 * deshalb automatisch abgeschlossen hat. `geloescht_am` = Tag der Erkennung (UTC), nicht der
 * Löschzeitpunkt in SF — den liefert die Papierkorb-Abfrage nicht.
 */
export const AuftragSfGeloeschtDatenSchema = z.object({
  project_key: z.string().min(1),
  name: z.string(),
  geloescht_am: z.string().date(),
});
export type AuftragSfGeloeschtDaten = z.infer<typeof AuftragSfGeloeschtDatenSchema>;

/**
 * `service_auftrag_verdeckt` (#130): je nicht führendem Auftrag mit Bereich Service an einem
 * Projekt, dessen wirksamer Bereich nicht Service ist. Der Bereich eines Projekts kommt nur vom
 * führenden Auftrag (oder dem Override) — der Service-Filter sieht so einen Auftrag nie.
 */
export const ServiceAuftragVerdecktDatenSchema = z.object({
  project_key: z.string().min(1),
  name: z.string(),
  fuehrender_project_key: z.string().nullable(),
});
export type ServiceAuftragVerdecktDaten = z.infer<typeof ServiceAuftragVerdecktDatenSchema>;

/** `besetzung_ohne_satz` (A6, L3): je Person — welcher Satz an welchem laufenden Auftrag fehlt. */
export const BesetzungOhneSatzDatenSchema = z.object({
  person_name: z.string().min(1),
  auftraege: z
    .array(
      z.object({
        project_key: z.string().min(1),
        abrechnungsart: z.string().min(1),
        fehlt: z.enum(['vk', 'ek']),
      }),
    )
    .min(1),
});
export type BesetzungOhneSatzDaten = z.infer<typeof BesetzungOhneSatzDatenSchema>;

/**
 * `besetzung_intern_und_extern` (L4, Gesamt-Review): ein Personenname ist unter den laufenden
 * Aufträgen eines Projekts sowohl intern als auch extern besetzt. Zwei mögliche Ursachen, aus den
 * Daten nicht unterscheidbar — dieselbe Person doppelt gebucht (Datenfehler in SF) oder zwei
 * gleichnamige Personen. `besetzung_ist` trägt keinen Personen-Schlüssel, nur den Namen.
 */
export const BesetzungInternUndExternDatenSchema = z.object({
  person_name: z.string().min(1),
  auftraege: z
    .array(
      z.object({
        project_key: z.string().min(1),
        seite: z.enum(['intern', 'extern']),
      }),
    )
    .min(2),
});
export type BesetzungInternUndExternDaten = z.infer<typeof BesetzungInternUndExternDatenSchema>;

export const HauptprojektGrundSchema = z.enum([
  'nicht_laufend',
  'ohne_budget',
  'andere_karte',
  'nicht_in_pv',
]);

/** `budget_fehlt` (A6, L3): je laufendem Auftrag; `budget` null = in SF leer, sonst ≤ 0. */
export const BudgetFehltDatenSchema = z.object({
  project_key: z.string().min(1),
  abrechnungsart: z.string().min(1),
  budget: z.number().nullable(),
  /** #118: Unterprojekt, dessen Hauptprojekt das Budget nicht (nutzbar) trägt — Grund für den Text */
  hauptprojekt: z
    .object({
      project_key: z.string().min(1),
      grund: HauptprojektGrundSchema,
      sf_projektstatus: z.string().nullable(),
    })
    .optional(),
});
export type BudgetFehltDaten = z.infer<typeof BudgetFehltDatenSchema>;

/** `abrechnungsart_gemischt` (A6, L3): die laufenden Aufträge mit ihren rohen Abrechnungsarten (Anzeige). */
export const AbrechnungsartGemischtDatenSchema = z.object({
  auftraege: z
    .array(z.object({ project_key: z.string().min(1), abrechnungsart: z.string().min(1) }))
    .min(2),
  arten: z.array(z.string().min(1)).min(2),
});
export type AbrechnungsartGemischtDaten = z.infer<typeof AbrechnungsartGemischtDatenSchema>;

/**
 * `final_blockiert` (L3): welche automatische „final"-Bedingung (A7, 1–3) verfehlt ist. Offene
 * Befunde (Bedingung 4) und die KFML-Entfernung (5) sind bewusst KEIN Grund.
 */
export const FinalBlockiertGrundSchema = z.discriminatedUnion('art', [
  z.object({
    art: z.literal('auftrag_nicht_abgeschlossen'),
    project_key: z.string().min(1),
    status: z.string().nullable(),
    abrechnungsart: z.string().nullable(),
  }),
  z.object({
    art: z.literal('buchung_nach_ende'),
    project_key: z.string().min(1),
    projektende: z.string().date(),
    letzte_buchung: z.string().date(),
  }),
  z.object({ art: z.literal('restbudget_fehlt') }),
]);
export type FinalBlockiertGrund = z.infer<typeof FinalBlockiertGrundSchema>;

export const FinalBlockiertDatenSchema = z.object({
  tage_seit_abschluss: z.number().int().nonnegative(),
  gruende: z.array(FinalBlockiertGrundSchema).min(1),
});
export type FinalBlockiertDaten = z.infer<typeof FinalBlockiertDatenSchema>;

/** `float_keine_planung` (A6, L4): laufende Aufträge mit SF-Ende ≥ heute; `float_projekte` = gefundene Float-Projekte zur P-Nummer. */
export const FloatKeinePlanungDatenSchema = z.object({
  auftraege: z
    .array(
      z.object({
        project_key: z.string().min(1),
        projektende: z.string().date(),
        float_projekte: z.number().int().nonnegative(),
      }),
    )
    .min(1),
});
export type FloatKeinePlanungDaten = z.infer<typeof FloatKeinePlanungDatenSchema>;

/** `float_nur_reservierung` (A6, L4): Reservierungs-Projekte mit Planung ab heute. */
export const FloatNurReservierungDatenSchema = z.object({
  reservierungen: z
    .array(
      z.object({
        name: z.string().min(1),
        project_key: z.string().min(1),
        stunden: z.number().positive(),
      }),
    )
    .min(1),
});
export type FloatNurReservierungDaten = z.infer<typeof FloatNurReservierungDatenSchema>;

/** `float_zuordnung_unklar` (A6, L4): je P-Nummer die gleichrangigen Float-Projekte. */
export const FloatZuordnungUnklarDatenSchema = z.object({
  project_key: z.string().min(1),
  kandidaten: z.array(z.string()).min(1),
});
export type FloatZuordnungUnklarDaten = z.infer<typeof FloatZuordnungUnklarDatenSchema>;

/** `float_person_ohne_besetzung` (A6, L4): je Person (Float-Name) ohne SF-Projektbesetzung. */
export const FloatPersonOhneBesetzungDatenSchema = z.object({
  person_name: z.string().min(1),
  stunden: z.number().positive(),
  float_projekte: z.array(z.string().min(1)).min(1),
});
export type FloatPersonOhneBesetzungDaten = z.infer<typeof FloatPersonOhneBesetzungDatenSchema>;

const BudgetPersonSchema = z.object({
  name: z.string().min(1),
  extern: z.boolean(),
  ist_h: z.number(),
  geplant_h: z.number(),
  ist_eur: z.number(),
  geplant_eur: z.number(),
});

const NachSfEndeSchema = z.object({
  project_key: z.string().min(1),
  projektende: z.string().date(),
  stunden: z.number().positive(),
});

/** Gemeinsamer Rechenweg der Budget-Regeln (A6, L4) — Beträge in €, auf Cent gerundet. */
export const BudgetBasisDatenSchema = z.object({
  budget: z.number().positive(),
  ist: z.number(),
  reserviert_extern: z.number(),
  geplant: z.number(),
  ergebnis: z.number(),
  personen: z.array(BudgetPersonSchema),
  /** Float-Stunden ohne Besetzung an einem laufenden Auftrag — nicht bewertet (Produktfrage 4). */
  ohne_besetzung_h: z.number().nonnegative(),
  /**
   * Fix-Runde 1 (Fix 3, #107): geplante Stunden interner Personen, deren Besetzung keinen
   * bewertbaren Satz hat — sonst stecken die Stunden unsichtbar in `geplant_h`, obwohl sie mit
   * 0 € in die Rechnung eingehen. `nonnegative()` wie die Schwester `ohne_besetzung_h`: 0 ist der
   * Normalfall, `rundeStunden` verhindert, dass eine Rundung eine echte Planung auf 0 kappt.
   */
  ohne_satz_h: z.number().nonnegative(),
  nach_sf_ende: z.array(NachSfEndeSchema),
  /** #118: Unterprojekte ohne eigenes Budget, deren Budget im Hauptprojekt mitgerechnet ist */
  ueber_hauptprojekt: z
    .array(z.object({ project_key: z.string().min(1), hauptprojekt: z.string().min(1) }))
    .optional(),
});
export type BudgetBasisDaten = z.infer<typeof BudgetBasisDatenSchema>;

export const PtBedarfSchema = z.discriminatedUnion('art', [
  z.object({
    art: z.literal('je_person'),
    personen: z.array(
      z.object({
        name: z.string().min(1),
        verbleibend_pt: z.number(),
        geplant_pt: z.number(),
        /** > 0: so viele PT mehr einplanen; < 0: so viele zu viel */
        differenz_pt: z.number(),
      }),
    ),
  }),
  z.object({ art: z.literal('gesamt'), pt: z.number(), vk_schnitt: z.number().positive() }),
  z.object({ art: z.literal('keiner') }),
]);
export type PtBedarf = z.infer<typeof PtBedarfSchema>;

/** `budget_rechnung` (A6, L4): Rechnung/Intern/Rahmenvertrag, bewertet mit dem VK. */
export const BudgetRechnungDatenSchema = BudgetBasisDatenSchema.extend({
  lage: z.enum(['ueberplant', 'unterplant']),
  /** überzogen nur durch die Reservierung für Externe */
  nur_wegen_externe: z.boolean(),
  pt_bedarf: PtBedarfSchema,
});
export type BudgetRechnungDaten = z.infer<typeof BudgetRechnungDatenSchema>;

/** `budget_festpreis` (A6, L4): Festpreis, bewertet mit dem EK. */
export const BudgetFestpreisDatenSchema = BudgetBasisDatenSchema.extend({
  marge_prozent: z.number(),
});
export type BudgetFestpreisDaten = z.infer<typeof BudgetFestpreisDatenSchema>;

// --- PL-Antwort (L2, schliess_check) -------------------------------------------

export const BefundAntwortArtSchema = z.enum(['schliessen', 'verlaengern', 'laeuft_weiter']);
export type BefundAntwortArt = z.infer<typeof BefundAntwortArtSchema>;

/** Form von `befund_verlauf.antwort` beim Ereignis `angefragt`. */
export const BefundAntwortGespeichertSchema = z.object({
  antwort: z.enum(['schliessen', 'verlaengern']),
  datum: z.string().date().nullable(),
});

/** Letzte PL-Antwort an einem angefragten Befund — Anzeige für die KFML. */
export const BefundPlAntwortSchema = BefundAntwortGespeichertSchema.extend({
  von_name: z.string().nullable(),
  am: IsoDateTimeSchema,
});
export type BefundPlAntwort = z.infer<typeof BefundPlAntwortSchema>;

// --- Anzeige (API → Web) -----------------------------------------------------

export const BefundAnzeigeSchema = z
  .object({
    befund_id: UuidSchema,
    project_id: UuidSchema,
    project_key: z.string(),
    customer_name: z.string(),
    project_name: z.string(),
    regel: BefundRegelKeySchema,
    regel_titel: z.string(),
    gegenstand: z.string(),
    stufe: BefundStufeSchema,
    zustand: BefundZustandSchema,
    erstmals_gesehen: IsoDateTimeSchema,
    zuletzt_gesehen: IsoDateTimeSchema,
    zurueckgestellt_bis: z.string().date().nullable(),
    erledigt_am: IsoDateTimeSchema.nullable(),
    erledigt_weg: BefundErledigtWegSchema.nullable(),
    /** Ein Satz mit den Zahlen — serverseitig gebaut (services/befunde/texte.ts). */
    satz: z.string(),
    /** Zur Laufzeit aufgelöst, nie gespeichert (A3). */
    zustaendig: z.object({ rolle: BefundZustaendigRolleSchema, user_ids: z.array(UuidSchema) }),
    aktionen: z.array(BefundAktionSchema),
    /** L2: letzte PL-Antwort (nur im Zustand `angefragt`), sonst `null`. */
    pl_antwort: BefundPlAntwortSchema.nullable(),
    /** Ergebnis von canDoProjectAction für den anfragenden Nutzer. */
    darf: z.object({
      zurueckstellen: z.boolean(),
      nachhaken: z.boolean(),
      briefing_erzeugen: z.boolean(),
      /** L2: PL-Antwort geben (`befund_antworten` + Regel kennt die Aktion). */
      antworten: z.boolean(),
      /** L2: „kann geschlossen werden" nur bei in der PV abgeschlossenem Projekt (A11). */
      antwort_schliessen: z.boolean(),
      /**
       * L4-Nutzerentscheid (#107): „Jetzt neu prüfen" verlangt `edit` am Projekt, nicht nur
       * `read` — der Knopf löst auch Float-Abfragen aus. Gilt unabhängig vom Befund-Zustand
       * (auch bei `erledigt`; die Zeile blendet den Knopf dort ohnehin über `zustand` aus).
       */
      neu_pruefen: z.boolean(),
    }),
  })
  .extend(HerkunftFelder);
export type BefundAnzeige = z.infer<typeof BefundAnzeigeSchema>;

export const BefundListeSchema = z.object({ items: z.array(BefundAnzeigeSchema) });
export type BefundListe = z.infer<typeof BefundListeSchema>;

export const BefundAufsichtGruppeSchema = z.object({
  /** user_id der zuständigen Person, sonst `rolle:<rolle>` (niemand / mehrere). */
  schluessel: z.string(),
  zustaendig_user_id: UuidSchema.nullable(),
  zustaendig_name: z.string().nullable(),
  rolle: BefundZustaendigRolleSchema,
  anzahl: z.number().int().positive(),
  hoechste_stufe: BefundStufeSchema,
  aeltester: IsoDateTimeSchema,
  befunde: z.array(BefundAnzeigeSchema),
});
export type BefundAufsichtGruppe = z.infer<typeof BefundAufsichtGruppeSchema>;

export const BefundAufsichtSchema = z.object({
  hat_aufsicht: z.boolean(),
  gruppen: z.array(BefundAufsichtGruppeSchema),
});
export type BefundAufsicht = z.infer<typeof BefundAufsichtSchema>;

export const BefundErledigtListeSchema = z.object({
  /** `letzter_lauf_start` — Einträge ab hier; `null` = noch nie gelaufen. */
  seit: IsoDateTimeSchema.nullable(),
  items: z.array(BefundAnzeigeSchema),
});
export type BefundErledigtListe = z.infer<typeof BefundErledigtListeSchema>;

/** Zählt nur aktive Befunde (wie der Stapel): offen, angefragt, abgelaufen zurückgestellt. */
export const BefundUebersichtEintragSchema = z.object({
  anzahl: z.number().int().positive(),
  hoechste_stufe: BefundStufeSchema,
  /**
   * Stufe + Titel je zählendem Befund (Stufe absteigend, ältester zuerst) — Tooltip am Marker
   * baut daraus eine Zeile je Befund, statt nur die höchste Stufe einmal vor allen zu nennen
   * (Feedback 1, L1: Stufen können sich zwischen den Befunden eines Projekts unterscheiden).
   */
  befunde: z.array(z.object({ stufe: BefundStufeSchema, titel: z.string() })),
});
export type BefundUebersichtEintrag = z.infer<typeof BefundUebersichtEintragSchema>;

export const BefundUebersichtSchema = z.object({
  items: z.record(UuidSchema, BefundUebersichtEintragSchema),
});
export type BefundUebersicht = z.infer<typeof BefundUebersichtSchema>;

export const BefundVerlaufEintragSchema = z.object({
  verlauf_id: UuidSchema,
  ereignis: BefundVerlaufEreignisSchema,
  von_zustand: BefundZustandSchema.nullable(),
  nach_zustand: BefundZustandSchema.nullable(),
  notiz: z.string().nullable(),
  /** Datum aus `antwort.bis` (Zurückstellen). */
  bis: z.string().date().nullable(),
  /** `null` = Prüflauf. */
  user_name: z.string().nullable(),
  am: IsoDateTimeSchema,
});
export type BefundVerlaufEintrag = z.infer<typeof BefundVerlaufEintragSchema>;

export const BefundVerlaufSchema = z.object({ items: z.array(BefundVerlaufEintragSchema) });
export type BefundVerlauf = z.infer<typeof BefundVerlaufSchema>;

// --- Eingaben ----------------------------------------------------------------

/**
 * POST /v1/befunde/:id/aktion. Die Pflichtfelder der PL-Antwort hängen an der Antwort und
 * werden deshalb in superRefine geprüft (discriminatedUnion kann nur ein `art` je Zweig).
 */
export const BefundAktionInputSchema = z
  .discriminatedUnion('art', [
    z.object({
      art: z.literal('zurueckstellen'),
      bis: z.string().date(),
      // .trim().min(1): ein Leerstring darf die Pflicht nie still aushebeln.
      grund: z.string().trim().min(1).max(2000),
    }),
    z.object({
      art: z.literal('nachhaken'),
      text: z.string().trim().max(2000).optional(),
    }),
    z.object({
      art: z.literal('antwort'),
      antwort: BefundAntwortArtSchema,
      datum: z.string().date().optional(),
      grund: z.string().trim().max(2000).optional(),
    }),
  ])
  .superRefine((v, ctx) => {
    if (v.art !== 'antwort') return;
    if (v.antwort !== 'schliessen' && !v.datum) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['datum'], message: 'datum_pflicht' });
    }
    // Fix-Runde 1 (#105 L2, Review): „kann geschlossen werden" kennt kein Datum — ein
    // mitgeschicktes Datum wird sonst stillschweigend ignoriert statt den Fehler zu zeigen.
    if (v.antwort === 'schliessen' && v.datum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['datum'],
        message: 'datum_nicht_erlaubt',
      });
    }
    // Leerstring nach trim zählt nicht als Begründung (Fehlerklasse „Leerstring schaltet ab").
    if (v.antwort === 'laeuft_weiter' && !v.grund) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['grund'], message: 'grund_pflicht' });
    }
  });
export type BefundAktionInput = z.infer<typeof BefundAktionInputSchema>;

export const BefundPruefenInputSchema = z.object({ project_id: UuidSchema });
export type BefundPruefenInput = z.infer<typeof BefundPruefenInputSchema>;

export const BefundListeQuerySchema = z.object({
  sicht: z.enum(['stapel', 'aufsicht', 'erledigt']),
  locale: BefundLocaleSchema.default('de'),
});
export const BefundLocaleQuerySchema = z.object({ locale: BefundLocaleSchema.default('de') });

// --- Admin -------------------------------------------------------------------

export const BefundLaufErgebnisSchema = z.object({
  neu: z.number().int().nonnegative(),
  erledigt: z.number().int().nonnegative(),
  offen: z.number().int().nonnegative(),
  fehler: z.array(z.string()),
  /** L4: Float erreichbar? `null` = keine Float-Regel aktiv. */
  float_ok: z.boolean().nullable(),
  float_status: z.string().nullable(),
  /** L4: Projekte mit erfolgreichem Float-Abruf. */
  float_projekte: z.number().int().nonnegative().default(0),
  /** L4: Projekte, deren Abruf scheiterte — ihre Float-/Budget-Befunde blieben unverändert. */
  float_fehler_projekte: z.number().int().nonnegative().default(0),
  /** L2: in diesem Lauf automatisch „final" gesetzte Projekte. Default für Ergebnisse aus L1. */
  final_gesetzt: z.number().int().nonnegative().default(0),
  /** L3: in diesem Lauf entzogene automatische „final"-Kennzeichen (Buchung nach SF-Ende). */
  final_entzogen: z.number().int().nonnegative().default(0),
});
export type BefundLaufErgebnis = z.infer<typeof BefundLaufErgebnisSchema>;

export const UpdateBefundPruefungEinstellungenInputSchema = z.object({
  enabled: z.boolean(),
  time: TimeOfDaySchema,
  weekdays: z.array(WeekdaySchema).min(1).max(7),
});
export type UpdateBefundPruefungEinstellungenInput = z.infer<
  typeof UpdateBefundPruefungEinstellungenInputSchema
>;

export const BefundPruefungEinstellungenSchema =
  UpdateBefundPruefungEinstellungenInputSchema.extend({
    timezone: z.string(),
    lauf_laeuft_seit: IsoDateTimeSchema.nullable(),
    letzter_lauf_start: IsoDateTimeSchema.nullable(),
    letzter_lauf_ende: IsoDateTimeSchema.nullable(),
    letzter_lauf_ergebnis: BefundLaufErgebnisSchema.nullable(),
  });
export type BefundPruefungEinstellungen = z.infer<typeof BefundPruefungEinstellungenSchema>;

export const BefundRegelAdminSchema = z.object({
  regel: BefundRegelKeySchema,
  titel: z.string(),
  beschreibung: z.string(),
  aktiv: z.boolean(),
  /** Nicht erledigte Befunde — auch pausierte zählen (A4: bleiben gespeichert). */
  offen: z.number().int().nonnegative(),
  geaendert_von_name: z.string().nullable(),
  geaendert_am: IsoDateTimeSchema.nullable(),
});
export type BefundRegelAdmin = z.infer<typeof BefundRegelAdminSchema>;

export const UpdateBefundRegelInputSchema = z.object({ aktiv: z.boolean() });
export type UpdateBefundRegelInput = z.infer<typeof UpdateBefundRegelInputSchema>;

/** POST /v1/projects/:id/final — KFML setzt „final" von Hand (A7), Begründung Pflicht. */
export const FinalSetzenInputSchema = z.object({ grund: z.string().trim().min(1).max(2000) });
export type FinalSetzenInput = z.infer<typeof FinalSetzenInputSchema>;
